import { UsageTrackerEvent, usageTracker } from '@volumental/analytic';
import { Container, Content } from '@volumental/common';
import { type JSX, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from '../../components/accessible-components';
import { useScannerStore, useViewStore, useSessionStore } from '../../store';
import { CurrentView } from '../../types/types';
import { UserMediaProvider } from './components/camera/user-media';
import { Capture } from './components/capture';
import { InstructionView } from './components/instruction-view';
import {
  CapturingStage,
  formatAsJpeg,
  type ScannerVariant,
  View,
} from './utils';

interface ScannerProps {
  onClose?: () => void;
  onBackToOnboarding: () => void;
  scannerVariant: ScannerVariant;
}

export function Scanner({
  onClose,
  onBackToOnboarding,
  scannerVariant,
}: ScannerProps): JSX.Element {
  const { t } = useTranslation();
  const { setBlobs, flowType } = useScannerStore((store) => store);
  const { setCurrentView } = useViewStore((store) => store);
  const [firstImage, setFirstImage] = useState<Blob>();
  const [stage, setStage] = useState(CapturingStage.Capture1);
  const { mode } = useSessionStore((store) => store);
  const onBack = useCallback(() => {
    switch (stage) {
      case CapturingStage.Capture1: {
        onBackToOnboarding();
        return;
      }
      case CapturingStage.Instruction: {
        setStage(CapturingStage.Capture1);
        return;
      }
      case CapturingStage.Capture2: {
        setStage(CapturingStage.Instruction);
      }
    }
  }, [stage, onBackToOnboarding]);

  const onFirstCapture = useCallback(
    (blob: Blob) => {
      setFirstImage(blob);
      usageTracker.track(UsageTrackerEvent.Capture1, {
        sizing_flow_type: flowType,
        experience: mode,
      });
      setStage(CapturingStage.Instruction);
    },
    [flowType, mode],
  );

  const onSecondCapture = useCallback(
    (blob: Blob) => {
      if (!firstImage) {
        throw new Error('First image is missing');
      }
      const files = [firstImage, blob].map((element) => formatAsJpeg(element));
      usageTracker.track(UsageTrackerEvent.Capture2, {
        sizing_flow_type: flowType,
        experience: mode,
      });
      setBlobs(files);
      setCurrentView(CurrentView.Reconstructing);
    },
    [firstImage, flowType, mode, setBlobs, setCurrentView],
  );

  const child = useMemo(() => {
    switch (stage) {
      case CapturingStage.Capture1: {
        return (
          <Capture
            view={View.View1}
            scannerVariant={scannerVariant}
            onValid={onFirstCapture}
          />
        );
      }
      case CapturingStage.Instruction: {
        return (
          <InstructionView
            scannerVariant={scannerVariant}
            onClick={() => {
              setStage(CapturingStage.Capture2);
            }}
          />
        );
      }
      case CapturingStage.Capture2: {
        return (
          <Capture
            view={View.View2}
            scannerVariant={scannerVariant}
            onValid={onSecondCapture}
          />
        );
      }
    }
  }, [stage, scannerVariant, onFirstCapture, onSecondCapture]);

  return (
    <Container>
      <Header
        back={onBack}
        close={onClose}
        title={
          stage === CapturingStage.Instruction
            ? undefined
            : t('instruction.samePhoto')
        }
      />
      <Content disableGutters disableScrollbar>
        <UserMediaProvider>{child}</UserMediaProvider>
      </Content>
    </Container>
  );
}
